import { InputParameterHierarchyType, InputParameterHierarchyTypeDisplayName } from '../model'
import React, { useState } from 'react'
import { OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { useZocksAuth } from '../../ScopedBlock/hooks'
import { Button } from '../../Button/Button'
import {
  InputParametersHierarchyContext,
  useDeleteNodeMutation,
  useGetTemplateReferencesByContextQuery,
  useGetTemplateReferencesForAllUsersByContextQuery,
} from '../../../store/ai/inputParametersHierarchy'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { InputParametersHierarchyLevel, InputParametersHierarchyNodeTemplateReference, TemplateReference } from '../../../models/InputParametersHierarchy'
import './TemplateSelectRow.scss'
import { useNavigate } from 'react-router-dom'
import { useTemplateSelection } from '../../../views/Setup/Templates/useTemplateSelection'
import { calculateLevel } from '../../../views/Setup/Templates/utils'
import { AllUserReferencesModal } from './AllUserReferencesModal'
import { MissingScopeAction } from '../../ScopedBlock/models'
import { ScopedBlock } from '../../ScopedBlock/ScopedBlock'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getInContextTrackingDetails } from '../../../views/Setup/Templates/tracking'
import { useAccount } from '../../../common/useAccount'
import { LOCK_ALL_OVERRIDE, LOCK_USER_OVERRIDE } from '../../../models/Account'

export interface TemplateSelectRowProps {
  title?: React.ReactNode
  templateSelectionSubHeader: string
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
  meetingTemplateName?: string
  disabled?: boolean
  disabledTooltipText?: string
}

export const TemplateSelectRow: React.FC<TemplateSelectRowProps> = ({
  title,
  templateSelectionSubHeader,
  type,
  typeId,
  context,
  meetingTemplateName,
  disabled = false,
  disabledTooltipText,
}) => {
  const { tracked } = useTracking()
  const { hasScope } = useZocksAuth()
  const navigate = useNavigate()
  const { selectTemplate } = useTemplateSelection()
  const [triggerDeleteNode, { isLoading: isDeleteNodeLoading }] = useDeleteNodeMutation()
  const [showAllUserReferencesModal, setShowAllUserReferencesModal] = useState(false)
  const header = `Choose ${InputParameterHierarchyTypeDisplayName[type]} Template`

  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')
  const { accountId, account } = useAccount()
  const { data: userReferences, isLoading } = useGetTemplateReferencesByContextQuery({
    accountId,
    type,
    typeId,
    context,
  })
  const isUserOverrideLocked = [LOCK_USER_OVERRIDE, LOCK_ALL_OVERRIDE].includes(account?.enterpriseConfig.lockTemplateOverride ?? '')
  const isGlobalOverrideLocked = account?.enterpriseConfig.lockTemplateOverride === LOCK_ALL_OVERRIDE
  const [globalReference, userReference] = userReferences ? extractGlobalAndUserReferences(context, userReferences.references) : [undefined, undefined]

  const onChooseUser = () => {
    selectTemplate({
      header,
      subHeader: templateSelectionSubHeader,
      type,
      typeId,
      context,
      templateId: userReference?.template?.id,
      nodeId: userReference?.id,
    })
  }

  const onRemoveUser = tracked(
    async () => {
      if (!userReference) {
        return
      }
      await triggerDeleteNode({ accountId, type, typeId, nodeId: userReference!.id! })
    },
    {
      eventName: TrackEvent.TEMPLATE_ASSIGNMENT_REMOVED,
    },
    {
      condition: !!userReference,
      eventDetailsExtractor: () => getInContextTrackingDetails(userReference!.template.id, type, typeId, context),
    },
  )

  const onChooseGlobal = () => {
    const globalContext = toGlobalContext(context)
    const isExistingAtExpectedLevel = globalReference?.hierarchyId.level === calculateLevel(type, globalContext)
    selectTemplate({
      header,
      subHeader: templateSelectionSubHeader,
      type,
      typeId,
      context: globalContext,
      templateId: globalReference?.template?.id || userReferences?.default?.id,
      nodeId: isExistingAtExpectedLevel ? globalReference?.id : undefined,
    })
  }

  const onOpenTemplate = (templateReference: TemplateReference, level: 'user' | 'global') => {
    const originContext = level === 'user' ? context : toGlobalContext(context)
    const existingNodeId = level === 'user' ? userReference?.id : globalReference?.id
    navigate(`/workspace/setup/templates/template/${templateReference.id}`, {
      state: { origin: { type, typeId, context: originContext, existingNodeId, meetingTemplateName } },
    })
  }

  return (
    <tr>
      {showAllUserReferencesModal && (
        <AllUserReferencesModal
          type={type}
          typeId={typeId}
          context={context}
          show={showAllUserReferencesModal}
          onHide={() => setShowAllUserReferencesModal(false)}
        />
      )}
      <td>{title || InputParameterHierarchyTypeDisplayName[type]}</td>
      <td>
        <GlobalReferenceButton
          templateReference={globalReference?.template}
          defaultTemplateReference={userReferences?.default}
          onOpen={(ref) => onOpenTemplate(ref, 'global')}
          onChoose={onChooseGlobal}
          isLoading={isLoading}
          readOnly={isGlobalOverrideLocked || disabled}
          readOnlyTooltipText={disabled ? disabledTooltipText : isGlobalOverrideLocked ? 'Template override is disabled at the enterprise level' : undefined}
          disableOpen={disabled}
        />
      </td>
      <td>
        <UserReferenceButton
          templateReference={userReference?.template}
          onOpen={(ref) => onOpenTemplate(ref, 'user')}
          onChoose={onChooseUser}
          onRemove={onRemoveUser}
          isLoading={isLoading || isDeleteNodeLoading}
          readOnly={isUserOverrideLocked || disabled}
          readOnlyTooltipText={disabled ? disabledTooltipText : isUserOverrideLocked ? 'Template override is disabled at the enterprise level' : undefined}
          disableOpen={disabled}
        />
      </td>
      {hasIpHierarchyAdminScope && (
        <td>
          <AllUserReferencesButton
            type={type}
            typeId={typeId}
            context={context}
            onOpen={() => setShowAllUserReferencesModal(true)}
            disabled={disabled}
            disabledTooltipText={disabled ? disabledTooltipText : undefined}
          />
        </td>
      )}
    </tr>
  )
}

interface UserReferenceButtonProps {
  templateReference?: TemplateReference
  onOpen: (templateReference: TemplateReference) => void
  onChoose: () => void
  onRemove: (templateReference: TemplateReference) => void
  isLoading: boolean
  readOnly?: boolean
  readOnlyTooltipText?: string
  disableOpen?: boolean
}

const UserReferenceButton: React.FC<UserReferenceButtonProps> = ({
  templateReference,
  onOpen,
  onChoose,
  onRemove,
  isLoading,
  readOnly = false,
  readOnlyTooltipText,
  disableOpen = false,
}) => {
  const [showIconButton, setShowIconButton] = useState(false)
  return templateReference ? (
    <div className='d-flex align-items-center flex-row gap-2' onMouseEnter={() => setShowIconButton(true)} onMouseLeave={() => setShowIconButton(false)}>
      <OverlayTrigger
        placement='top'
        overlay={<BootstrapTooltip>{disableOpen && readOnlyTooltipText ? readOnlyTooltipText : 'Open template'}</BootstrapTooltip>}
      >
        <span className='d-inline-block'>
          <Button
            className='templateSelectButton button-medium'
            variant='grayGhost'
            onClick={() => onOpen(templateReference)}
            loading={isLoading}
            disabled={disableOpen}
          >
            <div className='d-flex align-items-center'>
              <i className='bi bi-file-earmark-text pe-2' />
              <span className='d-none d-sm-inline template-name'>{templateReference.name}</span>
            </div>
          </Button>
        </span>
      </OverlayTrigger>
      <div className={showIconButton ? 'visible' : 'invisible'}>
        <OverlayTrigger
          placement='top'
          overlay={<BootstrapTooltip>{readOnly && readOnlyTooltipText ? readOnlyTooltipText : 'Remove your choice and use default'}</BootstrapTooltip>}
        >
          {({ ref, ...triggerHandler }) => (
            <ScopedBlock
              missingScopeAction={MissingScopeAction.UPGRADE}
              quota={{
                quota: 'ai.ipHierarchy.value',
              }}
            >
              <Button
                ref={ref}
                className='templateSelectButton button-medium p-2'
                variant='grayGhost'
                onClick={() => onRemove(templateReference)}
                loading={isLoading}
                disabled={isLoading || readOnly}
                {...triggerHandler}
              >
                <i className='bi bi-x-lg' />
              </Button>
            </ScopedBlock>
          )}
        </OverlayTrigger>
      </div>
    </div>
  ) : (
    <ChooseButton onChoose={onChoose} isLoading={isLoading} checkQuota readOnly={readOnly} readOnlyTooltipText={readOnlyTooltipText} />
  )
}

interface GlobalReferenceButtonProps {
  templateReference?: TemplateReference
  defaultTemplateReference?: TemplateReference
  onOpen: (templateReference: TemplateReference) => void
  onChoose: (templateReference?: TemplateReference) => void
  isLoading: boolean
  readOnly?: boolean
  readOnlyTooltipText?: string
  disableOpen?: boolean
}

const GlobalReferenceButton: React.FC<GlobalReferenceButtonProps> = ({
  templateReference,
  defaultTemplateReference,
  onOpen,
  onChoose,
  isLoading,
  readOnly = false,
  readOnlyTooltipText,
  disableOpen = false,
}) => {
  const { hasScope } = useZocksAuth()
  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')
  const effectiveTemplateReference = templateReference || defaultTemplateReference
  const [showIconButton, setShowIconButton] = useState(false)

  return effectiveTemplateReference ? (
    <div className='d-flex align-items-center flex-row gap-2' onMouseEnter={() => setShowIconButton(true)} onMouseLeave={() => setShowIconButton(false)}>
      <OverlayTrigger
        placement='top'
        overlay={<BootstrapTooltip>{disableOpen && readOnlyTooltipText ? readOnlyTooltipText : 'Open template'}</BootstrapTooltip>}
      >
        <span className='d-inline-block'>
          <Button className='templateSelectButton button-medium' variant='grayGhost' onClick={() => onOpen(effectiveTemplateReference)} disabled={disableOpen}>
            <div className='d-flex align-items-center'>
              <i className='bi bi-file-earmark-text pe-2' />
              <span className='d-none d-sm-inline template-name'>{effectiveTemplateReference.name}</span>
            </div>
          </Button>
        </span>
      </OverlayTrigger>
      {hasIpHierarchyAdminScope ? (
        <div className={showIconButton ? 'visible' : 'invisible'}>
          <OverlayTrigger
            placement='top'
            overlay={<BootstrapTooltip>{readOnly && readOnlyTooltipText ? readOnlyTooltipText : 'Change default'}</BootstrapTooltip>}
          >
            <Button
              className='templateSelectButton button-medium p-2'
              variant='grayGhost'
              onClick={() => onChoose(effectiveTemplateReference)}
              disabled={readOnly}
            >
              <i className='bi bi-pencil' />
            </Button>
          </OverlayTrigger>
        </div>
      ) : null}
    </div>
  ) : (
    <ChooseButton onChoose={onChoose} isLoading={isLoading} readOnly={readOnly} readOnlyTooltipText={readOnlyTooltipText} />
  )
}

interface ChooseButtonProps {
  onChoose: (templateReference?: TemplateReference) => void
  checkQuota?: boolean
  isLoading: boolean
  readOnly?: boolean
  readOnlyTooltipText?: string
}

const ChooseButton: React.FC<ChooseButtonProps> = ({ onChoose, checkQuota = false, isLoading, readOnly = false, readOnlyTooltipText }) => {
  return checkQuota ? (
    <OverlayTrigger
      placement='top'
      overlay={<BootstrapTooltip>{readOnly && readOnlyTooltipText ? readOnlyTooltipText : 'Choose a template to override the default'}</BootstrapTooltip>}
    >
      {({ ref, ...triggerHandler }) => (
        <ScopedBlock
          missingScopeAction={MissingScopeAction.UPGRADE}
          quota={{
            quota: 'ai.ipHierarchy.value',
          }}
        >
          <Button
            ref={ref}
            className='chooseButton button-medium'
            variant='grayGhost'
            onClick={() => onChoose()}
            loading={isLoading}
            {...triggerHandler}
            disabled={readOnly}
          >
            Choose a template
          </Button>
        </ScopedBlock>
      )}
    </OverlayTrigger>
  ) : (
    <OverlayTrigger
      placement='top'
      overlay={<BootstrapTooltip>{readOnly && readOnlyTooltipText ? readOnlyTooltipText : 'Choose a template to override the default'}</BootstrapTooltip>}
    >
      <Button className='chooseButton button-medium' variant='grayGhost' onClick={() => onChoose()} loading={isLoading} disabled={readOnly}>
        Choose a template
      </Button>
    </OverlayTrigger>
  )
}

interface AllUserReferencesButtonProps {
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
  onOpen: () => void
  disabled?: boolean
  disabledTooltipText?: string
}

const AllUserReferencesButton: React.FC<AllUserReferencesButtonProps> = ({ type, typeId, context, onOpen, disabled = false, disabledTooltipText }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: allReferences, isLoading } = useGetTemplateReferencesForAllUsersByContextQuery({ accountId, type, typeId, context }, { skip: disabled })
  const label = allReferences?.length ? (allReferences.length == 1 ? '1 user' : `${allReferences.length} users`) : 'Assign to users'

  const button = (
    <Button className='button-medium allUserReferencesButton' variant='borderless' onClick={onOpen} loading={isLoading} disabled={disabled}>
      {label}
    </Button>
  )

  if (disabled && disabledTooltipText) {
    return (
      <OverlayTrigger placement='top' overlay={<BootstrapTooltip>{disabledTooltipText}</BootstrapTooltip>}>
        <span>{button}</span>
      </OverlayTrigger>
    )
  }
  return button
}

const extractGlobalAndUserReferences = (
  context: InputParametersHierarchyContext,
  references: { [key in InputParametersHierarchyLevel]: InputParametersHierarchyNodeTemplateReference },
): [InputParametersHierarchyNodeTemplateReference | undefined, InputParametersHierarchyNodeTemplateReference | undefined] => {
  if ('meetingTemplateId' in context && context.meetingTemplateId) {
    return [
      references[InputParametersHierarchyLevel.MEETING_TEMPLATE_GLOBAL] ?? references[InputParametersHierarchyLevel.GLOBAL],
      references[InputParametersHierarchyLevel.MEETING_TEMPLATE_USER] ?? references[InputParametersHierarchyLevel.USER],
    ]
  } else if ('emailTypeId' in context && context.emailTypeId) {
    return [
      references[InputParametersHierarchyLevel.EMAIL_TYPE_GLOBAL] ?? references[InputParametersHierarchyLevel.GLOBAL],
      references[InputParametersHierarchyLevel.EMAIL_TYPE_USER] ?? references[InputParametersHierarchyLevel.USER],
    ]
  } else if ('documentTypeId' in context && context.documentTypeId) {
    return [
      references[InputParametersHierarchyLevel.DOCUMENT_TYPE_GLOBAL] ?? references[InputParametersHierarchyLevel.GLOBAL],
      references[InputParametersHierarchyLevel.DOCUMENT_TYPE_USER] ?? references[InputParametersHierarchyLevel.USER],
    ]
  }
  return [references[InputParametersHierarchyLevel.GLOBAL], references[InputParametersHierarchyLevel.USER]]
}

const toGlobalContext = (context: InputParametersHierarchyContext): InputParametersHierarchyContext => {
  const globalContext = { ...context }
  delete globalContext.userId
  return globalContext
}
